import { v4 as uuidv4 } from 'uuid';

import { getIsReportNameExists } from '../../api/report-designer';
import { WARNING_SOURCES } from '../../constants/WarningsConstants';
import { translate } from '../../form-designer/utils/form-manager.utils';
import { REPORT_CONTROL_TYPE } from '../constants/report-designer.constants';
import {
  MAX_REPORT_NAME_LENGTH,
  REPORT_CREATION_VALIDATION_ERROR_MESSAGES,
  VALIDATION_PROPS_BY_CONTROL_TYPE,
} from '../constants/validation.constants';

export const isFieldPropUnique = ({ items = [], prop, value }) => {
  const itemsByProp = items.filter((item) => item[prop]?.toUpperCase() === value?.toUpperCase());
  return itemsByProp.length === 1;
};

export const addError = ({
  validationErrors = [],
  translations = {},
  prop,
  instanceId = 'reportDesigner',
  message = '',
}) => {
  validationErrors.push({
    errorId: uuidv4(),
    instanceId,
    controlInfo: {
      prop,
    },
    message: `${translate(translations, message)}`,
    source: WARNING_SOURCES.VALIDATION,
  });
};

export const isValidName = (name = '') => {
  return RegExp('^(a-z|A-Z|0-9)*[^\\/:*?""<>,|\'%]*$').test(name);
};

const validateCondition = (
  conditionFunc,
  control,
  validationDetails,
  validationErrors,
  translations,
  reportModel
) => {
  const hasError = conditionFunc(control, reportModel);

  hasError &&
    addError({
      validationErrors,
      translations,
      instanceId: control.id || control.controlId,
      prop: validationDetails?.prop,
      message: validationDetails.errorMessage,
    });
};

export const validateControls = ({
  items = [],
  controlsValidationProps = [],
  validationErrors = [],
  translations = {},
  reportModel,
}) => {
  items.forEach((item) => {
    controlsValidationProps.forEach((validationDetails) => {
      validationDetails.condition &&
        validateCondition(
          validationDetails.condition,
          item,
          validationDetails,
          validationErrors,
          translations,
          reportModel
        );
    });
  });
};

const getDistributionItemsToValidate = ({ startDate, distributionList, controlId }) => {
  const distributionListToValidate = distributionList.map(({ code, id }) => ({
    distributionList,
    code,
    controlId: id,
  }));

  return [{ startDate, controlId }, ...distributionListToValidate];
};

export const validateReportCreation = async ({ reportName = '', reportModelName = '' }) => {
  const validationErrors = [];

  if (!reportModelName) {
    validationErrors.push({
      errorSource: 'reportModelName',
      message: REPORT_CREATION_VALIDATION_ERROR_MESSAGES.REPORT_MODEL_REQUIRED,
    });
  }

  if (!reportName) {
    validationErrors.push({
      errorSource: 'reportName',
      message: REPORT_CREATION_VALIDATION_ERROR_MESSAGES.NAME_REQUIRED,
    });

    return validationErrors;
  }

  if (reportName.length > MAX_REPORT_NAME_LENGTH) {
    validationErrors.push({
      errorSource: 'reportName',
      message: REPORT_CREATION_VALIDATION_ERROR_MESSAGES.MAX_LENGTH,
    });

    return validationErrors;
  }

  if (!isValidName(reportName)) {
    validationErrors.push({
      errorSource: 'reportName',
      message: REPORT_CREATION_VALIDATION_ERROR_MESSAGES.SPECIAL_CHARACTERS,
    });

    return validationErrors;
  }

  const response = await getIsReportNameExists(reportName);

  if (response.exists) {
    validationErrors.push({
      errorSource: 'reportName',
      message: REPORT_CREATION_VALIDATION_ERROR_MESSAGES.UNIQUE,
    });
  }

  return validationErrors;
};

export const validateReportControls = async ({ reportModel, translations = {} }) => {
  const validationErrors = [];

  Object.values(reportModel).forEach((control) => {
    const { controlType } = control;
    const validationConfig = VALIDATION_PROPS_BY_CONTROL_TYPE[controlType];

    if (validationConfig) {
      const controlsToValidate =
        controlType === REPORT_CONTROL_TYPE.DISTRIBUTION
          ? getDistributionItemsToValidate(control)
          : [control];

      validateControls({
        items: controlsToValidate,
        controlsValidationProps: validationConfig,
        validationErrors,
        translations,
        reportModel,
      });
    }
  });

  return validationErrors;
};

export const validateReportNameOnBlur = (reportName) => {
  if (!reportName.length) {
    return { hasError: true, errorText: REPORT_CREATION_VALIDATION_ERROR_MESSAGES.NAME_REQUIRED };
  }

  if (!isValidName(reportName)) {
    return {
      hasError: true,
      errorText: REPORT_CREATION_VALIDATION_ERROR_MESSAGES.SPECIAL_CHARACTERS,
    };
  }

  if (reportName.length > 100) {
    return { hasError: true, errorText: REPORT_CREATION_VALIDATION_ERROR_MESSAGES.MAX_LENGTH };
  }

  return { hasError: false, errorText: '' };
};

export const getContainsInvalidControlsForSavedSearch = (reportModel = {}) => {
  const { topChartContainer, crossTabContainer, groupHeaderContainer } = reportModel;

  if (
    topChartContainer.childrenNodes?.length ||
    crossTabContainer?.length ||
    groupHeaderContainer?.length
  ) {
    return true;
  }

  const expressionStartsWith = 'eval||';
  if (JSON.stringify(reportModel).includes(expressionStartsWith)) {
    return true;
  }

  return false;
};
